import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { getUserIsAuthed, getUserRole } from 'entities/User';
import { UserRole } from 'entities/User/model/consts/userConsts';
import { routePermissionSelectors } from 'entities/User/model/selectors/userPermissionsSeletors';
import {
    getUserHasPMCId,
    getUserRoleIsAcceptable,
} from 'entities/User/model/selectors/userSelectors';
import {
    getRouteAccessDenied,
    getRouteForbidden,
    getRouteMain,
} from 'shared/const/router';

import type { StateSchema } from 'app/providers/StoreProvider/config/StateSchema';

interface RequireAuthProps {
    children: JSX.Element;
    roles?: UserRole[];
    permission?: string;
}

export function RequireAuth({
    children,
    roles,
    permission,
}: Readonly<RequireAuthProps>) {
    const auth = useSelector(getUserIsAuthed);
    const userRole = useSelector(getUserRole);
    const haveAccessToApp = useSelector(getUserRoleIsAcceptable);
    const userHasPMC = useSelector(getUserHasPMCId);
    const hasPermission = useSelector((state: StateSchema) =>
        permission
            ? routePermissionSelectors[permission]?.(state) ?? false
            : true,
    );

    const hasRequiredRole = useCallback(
        (userRole: UserRole) => roles?.includes(userRole) ?? false,
        [roles],
    );

    if (!auth) {
        return <Navigate to={getRouteMain()} replace />;
    }

    if ((auth && !haveAccessToApp) || !userHasPMC) {
        return <Navigate to={getRouteAccessDenied()} replace />;
    }

    if (permission && !hasPermission) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!permission && !!roles && !hasRequiredRole(userRole)) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    return children;
}
